import Configs from '../constants/configs';
import Constants from '../constants/constants';
import * as moment from 'moment-timezone';
import * as _ from 'lodash-es';
import swal from 'sweetalert2';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

interface Address {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  county: string;
}

export default class Utils {

  static isValidEmail(emailAddress: string) {
    const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
    return emailPattern.test(emailAddress);
  }

  static isValidTel(value: any) {
    if (!value) {
      return value;
    }
    const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;

    return re.test(value);
  }

  static utc2HQDate(date: any) {
    if (!date) {
      return null;
    }

    if (!moment(date).isValid) {
      return null;
    }

    return moment(date)
      .tz(Configs.hq.timezone)
      .format(Configs.format.date);
  }

  static stringHQDate(date: any) {
    if (!date) {
      return null;
    }

    if (!moment(date).isValid) {
      return null;
    }

    return moment(date)
      .format(Configs.format.date);
  }

  static tel(tel: any) {
    if (!tel) {
      return '';
    }

    const value = tel
      .toString()
      .trim()
      .replace(/\D/g, '');

    if (value.match(/[^0-9]/)) {
      return tel;
    }

    let country, city, number;

    switch (value.length) {
      case 10: // +1PPP####### -> C (PPP) ###-####
        country = 1;
        city = value.slice(0, 3);
        number = value.slice(3);
        break;

      case 11: // +CPPP####### -> CCC (PP) ###-####
        country = value[0];
        city = value.slice(1, 4);
        number = value.slice(4);
        break;

      case 12: // +CCCPP####### -> CCC (PP) ###-####
        country = value.slice(0, 3);
        city = value.slice(3, 5);
        number = value.slice(5);
        break;

      default:
        return tel;
    }

    if (country === 1) {
      country = '';
    }

    number = number.slice(0, 3) + '-' + number.slice(3);

    return (country + ' (' + city + ') ' + number).trim();
  }

  static fullAddress(address: Address, lineBreaker: string = '<br >') {
    const lines = [];

    const { address1, address2, city, state, zipcode } = address || {};
    if (address1) {
      lines.push(address1);
    }
    if (address2) {
      lines.push(address2);
    }
    if (city || state || zipcode) {
      lines.push([
        this.cityState(address),
        zipcode].filter(x => !!x).join(' '));
    }

    return lines.join(lineBreaker);
  }

  static cityState(address: Address) {
    const { city, state } = address || {};
    return [city, state].map((x, idx) => {
      switch (idx) {
        case 1: // state
          return (x || '').toUpperCase();
        default:
          return x;
      }
    }).filter(x => !!x).join(', ');
  }

  static ampmTime(time: any) {
    if (!time) {
      return null;
    }

    let hourString = time.substring(0, 2);
    const beforeHourNumber = hourString * 1;

    let afterHourNumber;

    if (hourString > 12) {
      afterHourNumber = beforeHourNumber - 12;
      hourString = afterHourNumber + '';
    }
    const minute = time.substring(5, 2);
    return beforeHourNumber <= 11 ? hourString.concat(minute, ' am') : hourString.concat(minute, ' pm');
  }

  static localDate(date: any) {
    if (!date) {
      return null;
    }

    if (!moment(date).isValid) {
      return null;
    }

    return moment(date).format(Configs.format.date);
  }

  static stringToNgbDate(dateString: any): NgbDate | null {
    if (!dateString) {
      return dateString
    }
    const date = moment(dateString, 'YYYY-MM-DD');
    return new NgbDate(date.year(), date.month() + 1, date.day());
  }

  static dateToNgbDate(date: any): NgbDate | null {
    if (!date) {
      return date
    }
    const m = moment(date);
    return new NgbDate(m.year(), m.month() + 1, m.day());
  }

  static NgbDateToString(date: any, format = 'YYYY-MM-DD'): string {
    if (!date) {
      return date;
    }
    return moment(new Date(date.year, date.month - 1, date.day)).format(format);
  }

  static errorMessage(err: any): string {
    let text: any = null;
    const json = JSON.stringify(err, null, 2);
    if (err) {
      switch (typeof err) {
        case 'string':
          text = err;
          break;
        case 'object':
          if (err.messages) {
            text = err.messages.join('\n');
          }

          if (err.error) {
            if (!text && err.error.messages) {
              text = err.error.messages.join('\n');
            }

            if (!text && err.error.message) {
              text = err.error.message;
              const statusCode = err.error.statusCode || err.status;
              if (statusCode && text.startsWith(`${statusCode} `)) {
                try {
                  // message: 400 - {....
                  const tokens = text.split('-');
                  tokens.shift(); // remove the first
                  const json = JSON.parse(tokens.join('-'));
                  if (json.messages) {
                    text = json.messages.join('\n');
                  } else if (json.message) {
                    text = json.message;
                  }
                } catch (err) {
                  // do nothing
                }
              }
            }

            if (!text && err.error.error) {
              text = err.error.error;
            }
          }

          if (!text && err.message) {
            text = err.message;
          }

          if (!text && err.messages) {
            text = err.messages.join('\n');
          }

          if (!text && err.name === 'HttpErrorResponse') {
            text = `Fail to connect ${err.url || 'server'}`;
          }
          break;
        default:
          text = JSON.stringify(err);
          break;
      }
    }

    // if message is json, try to get message field
    return text || 'Error while processing...';
  }

  static showError(err: any) {
    console.error(err);
    return swal.fire({
      title: `Error!`,
      text: this.errorMessage(err),
      icon: `error`,
      customClass: {
        confirmButton: 'btn btn-primary'
      },
      buttonsStyling: false,
    });
  }

  static showMessage(message: string) {
    return swal.fire({
      title: 'Export',
      text: message,
      icon: 'success',
      customClass: {
        confirmButton: 'btn btn-primary'
      },
      buttonsStyling: false,
    });

  }

  static showConfirm(message: string, okTitle?: string, cancelTitle?: string) {
    return new Promise(resolve => {
      swal.fire({
        text: message,
        icon: 'question',
        showConfirmButton: true,
        confirmButtonText: okTitle || 'OK',
        showDenyButton: true,
        denyButtonText: cancelTitle || 'Cancel',
        customClass: {
          confirmButton: 'btn btn-primary mr-1',
          denyButton: 'btn btn-secondary',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.isConfirmed) {
          resolve(okTitle || true);
        } else if (result.isDenied) {
          resolve(cancelTitle || false);
        }
      });
      ;
    });
  }

  static getWeekdayName(day: number) {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day];
  }

  static formatTime(time: any) {
    if (!time) {
      return '';
    }

    const date = moment(moment().format('YYYYMMDD') + time, 'YYYYMMDDHHmmss');
    return date.format('h:mm A');
  }

  static isEmpty(obj: any) {
    if (typeof obj === 'undefined' || obj === null) {
      return true;
    }

    if (obj.constructor === {}.constructor) {
      return Object.keys(obj).map(x => obj[x]).every(x => typeof x === 'undefined' || x === null);
    }

    if (obj.constructor === [].constructor) {
      return obj.every((x: any) => typeof x === 'undefined' || x === null);
    }

    return false;
  }

  static trimObject(obj: any) {
    if (!obj) {
      return null;
    }

    return Object.keys(obj).reduce((ret: any, key) => {
      const value = obj[key];
      if (typeof value === 'undefined' || value === null) {
        return ret;
      }

      if (value.constructor === {}.constructor) {
        if (!Object.keys(value)) {
          return ret;
        }
      }

      if (value.constructor === [].constructor) {
        if (!value.length) {
          return ret;
        }
      }
      ret[key] = value;
      return ret;
    }, {});
  }
  static slugify(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  }
}
